import React, { Fragment, useEffect, useState } from "react";
import { PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from '@headlessui/react';
import submissionBoardService from "../../services/SubmissionBoardService";
import { showToast } from "../../shared/Toast";
import { SubmissionBoardModel } from "./SbModel";
import submissionKitService from "../../services/SubmissionKitService";
import Image from "../../Image";

interface SbMediaRequestNotesProps {
  onClose: () => void;
  project:any;
  submissionBoardModel: SubmissionBoardModel;
  profileUserInfo:any;
   defaultUserAvatar: string;
}

const SbMediaRequestNote: React.FC<SbMediaRequestNotesProps> = ({
  onClose,
  project,
  submissionBoardModel,
  profileUserInfo,
  defaultUserAvatar,
}) => {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSubmissionBoard, setSelectedSubmissionBoard] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (
      submissionBoardModel == undefined ||
      submissionBoardModel == null ||
      project == undefined ||
      project == null
    ) {
      return;
    }
    setSelectedSubmissionBoard(submissionBoardModel);
    setSelectedProject(project);
    setShowDialog(true);
  }, [submissionBoardModel]);

  const saveClickHandler = async () => {
    if (!note.trim()) {
      showToast('Note cannot be empty', 'danger');
      return;
    }
    try {
      if (
        selectedProject == undefined ||
        selectedProject == null ||
        selectedSubmissionBoard == undefined ||
        selectedSubmissionBoard == null
      ) {
        throw new Error('Error: Invalid data selection.');
      }
      setLoading(true);

      try {
        const response = await submissionKitService.askPerformerToUploadMedia(
          project.uuid,
          selectedSubmissionBoard.id,
          profileUserInfo.id,
          note
        );
        if (response)
        {
          showToast(
            'Sent media request to ' + profileUserInfo.firstName + ' ' + profileUserInfo.lastName,
            'success'
          );
          handleCancel();
          return;
        }
        showToast('Failed to send media request.', 'danger');
      } catch (error) {
        console.error('Error sending media request:', error);
        const errorMsg = error.response?.data?.error;
        showToast(errorMsg ?? 'Failed to send media request, please try again', 'danger');
      }
    } catch (error) {
      showToast('Failed to add note', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setNote('');
    setSelectedSubmissionBoard(null);
    setShowDialog(false);
    onClose();
  };

  return (
    <>
      <Transition.Root show={showDialog} as={Fragment}>
        <Dialog as="div" className="relative " style={{ zIndex: 20 }} onClose={handleCancel}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {/* The backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0  ">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative flex flex-col ovew-[380px]h- [418px] absolute top-[303px] left-[530px] rotate-0 opacity-100  rounded-[6px] border border-solid border-gray-300  bg-white shadow-[0px_19px_40px_-8px_rgba(0,0,0,0.32)] flex flex-col gap-[10px]rflow-y-auto bg-white py-1">
                  <div className="flex items-center justify-between px-4 py-2">
                    <h2 className="text-lg font-medium text-gray-900">Media Request</h2>
                    <button
                      id={'inviteprofiles_close_model_id'}
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded-md "
                      onClick={handleCancel}
                    >
                      <span className="sr-only">Close menu</span>
                      <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="mt-4 justify-items-center text-left">
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                      <div
                        className="bg-white rounded-lg shadow-xl flex flex-col overflow-visible"
                        style={{
                          height: '80vh',
                          maxHeight: '500px',
                          width: '80vw',
                          maxWidth: '500px',
                        }}
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                          <h2 className="text-xl font-semibold text-gray-900">Media Request</h2>
                          <button
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={onClose}
                          >
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 scrollable-content overflow-y-scroll" style={{}}>
                          <div className="p-6 ">
                            {/* Profile Info */}
                            <div className="mb-6 flex flex-row gap-2">
                             <div className="flex items-center ">
                                <Image
                                  className="rounded-lg w-14 h-14 object-cover"
                                  src={profileUserInfo?.imageUrl || defaultUserAvatar}
                                  fallbackSrc={defaultUserAvatar}
                                  alt={profileUserInfo?.firstName || 'Profile'}
                                />
                              </div>

                              <div className="flex flex-col items-start ">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  {'To: '}
                                  {profileUserInfo.firstName + ' ' + profileUserInfo.lastName}
                                </h3>
                                <p className="text-gray-600 text-sm">
                                  {submissionBoardModel.title}
                                </p>
                              </div>
                            </div>

                            {/* Add Note Section */}
                            <div className="mb-6">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Add a Message
                              </label>
                              <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Enter the request message..."
                                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                                rows={4}
                              />
                            </div>
                            {/* Footer */}
                            <div className="flex items-center justify-center gap-3 px-4 py-3 rounded-b-lg">
                              <button
                                onClick={handleCancel}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={saveClickHandler}
                                disabled={loading}
                                className="flex flex-row gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <PaperAirplaneIcon className="w-6 h-6" />
                                {loading ? 'Saving...' : 'Send Request'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default SbMediaRequestNote;